import { useEffect, useRef, type HTMLAttributes } from "react";
import { Color, Mesh, Program, Renderer, Triangle } from "ogl";
import "./Galaxy.css";

const vertexShader = `
attribute vec2 uv;
attribute vec2 position;

varying vec2 vUv;

void main() {
  vUv = uv;
  gl_Position = vec4(position, 0.0, 1.0);
}
`;

const fragmentShader = `
precision highp float;

uniform float uTime;
uniform vec3 uResolution;
uniform vec2 uFocal;
uniform vec2 uRotation;
uniform float uStarSpeed;
uniform float uDensity;
uniform float uHueShift;
uniform float uSpeed;
uniform vec2 uMouse;
uniform float uGlowIntensity;
uniform float uSaturation;
uniform bool uMouseRepulsion;
uniform float uTwinkleIntensity;
uniform float uRotationSpeed;
uniform float uRepulsionStrength;
uniform float uMouseActiveFactor;
uniform float uAutoCenterRepulsion;
uniform bool uTransparent;
uniform float uLightMode;

varying vec2 vUv;

#define NUM_LAYERS 4.0
#define MAT45 mat2(0.7071, -0.7071, 0.7071, 0.7071)

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float starShape(vec2 uv, float flare, float glow) {
  float distanceToCenter = max(length(uv), 0.001);
  float star = (0.012 + glow * 0.018) / distanceToCenter;
  float rays = smoothstep(0.0, 1.0, 1.0 - abs(uv.x * uv.y * 900.0));
  star += rays * flare * (0.16 + glow * 0.35);

  uv *= MAT45;
  rays = smoothstep(0.0, 1.0, 1.0 - abs(uv.x * uv.y * 900.0));
  star += rays * flare * (0.05 + glow * 0.16);
  return star * smoothstep(0.52, 0.08, distanceToCenter);
}

vec3 starTemperature(float seed) {
  vec3 cool = vec3(0.58, 0.76, 1.0);
  vec3 neutral = vec3(1.0, 0.98, 0.91);
  vec3 warm = vec3(1.0, 0.72, 0.46);
  float shifted = fract(seed + uHueShift / 720.0);
  vec3 temperature = shifted < 0.2
    ? mix(warm, neutral, shifted / 0.2)
    : mix(neutral, cool, smoothstep(0.2, 1.0, shifted));
  float luminance = dot(temperature, vec3(0.299, 0.587, 0.114));
  return mix(vec3(luminance), temperature, uSaturation);
}

vec3 starLayer(vec2 uv, float layer) {
  vec3 color = vec3(0.0);
  vec2 gridPosition = fract(uv) - 0.5;
  vec2 gridId = floor(uv);

  for (int y = -1; y <= 1; y++) {
    for (int x = -1; x <= 1; x++) {
      vec2 cellOffset = vec2(float(x), float(y));
      vec2 cellId = gridId + cellOffset + layer * 137.31;
      float seed = hash21(cellId);
      vec2 jitter = vec2(hash21(cellId + 17.7), hash21(cellId + 41.3)) - 0.5;
      float size = pow(hash21(cellId + 8.6), 5.0);
      float flare = smoothstep(0.84, 1.0, size);
      vec2 starUv = gridPosition - cellOffset - jitter * 0.72;
      float twinkle = sin(uTime * uSpeed * (0.7 + seed * 1.4) + seed * 38.0) * 0.5 + 0.5;
      twinkle = mix(1.0, 0.7 + twinkle * 0.6, uTwinkleIntensity);
      float star = starShape(starUv, flare, uGlowIntensity);
      color += star * mix(0.08, 0.95, size) * twinkle * starTemperature(seed);
    }
  }

  return color;
}

void main() {
  vec2 focalPx = uFocal * uResolution.xy;
  vec2 uv = (vUv * uResolution.xy - focalPx) / uResolution.y;
  vec2 mouseNorm = uMouse - vec2(0.5);

  if (uAutoCenterRepulsion > 0.0) {
    float centerDistance = max(length(uv), 0.08);
    uv += normalize(uv) * (uAutoCenterRepulsion / centerDistance) * 0.012;
  } else if (uMouseRepulsion) {
    vec2 mouseUv = (uMouse * uResolution.xy - focalPx) / uResolution.y;
    vec2 fromMouse = uv - mouseUv;
    float mouseDistance = max(length(fromMouse), 0.08);
    uv += normalize(fromMouse) * (uRepulsionStrength / mouseDistance) * 0.008 * uMouseActiveFactor;
  } else {
    uv += mouseNorm * 0.026 * uMouseActiveFactor;
  }

  float autoRotation = uTime * uRotationSpeed;
  mat2 slowRotation = mat2(cos(autoRotation), -sin(autoRotation), sin(autoRotation), cos(autoRotation));
  uv = slowRotation * uv;
  uv = mat2(uRotation.x, -uRotation.y, uRotation.y, uRotation.x) * uv;

  vec3 color = vec3(0.0);
  for (float layer = 0.0; layer < NUM_LAYERS; layer += 1.0) {
    float depth = layer / (NUM_LAYERS - 1.0);
    float scale = mix(7.0, 28.0, depth) * uDensity;
    float drift = uTime * uStarSpeed * mix(0.003, 0.0008, depth);
    vec2 layerUv = uv * scale + vec2(drift, -drift * 0.42) + layer * 113.73;
    color += starLayer(layerUv, layer) * mix(1.0, 0.42, depth);
  }

  if (uLightMode > 0.5) {
    float energy = max(max(color.r, color.g), color.b);
    float coverage = clamp(smoothstep(0.0, 0.42, energy) * 0.92, 0.0, 0.92);
    gl_FragColor = vec4(mix(vec3(1.0), clamp(color * 0.48, 0.0, 0.82), coverage), 1.0);
  } else if (uTransparent) {
    float alpha = smoothstep(0.015, 0.32, length(color));
    gl_FragColor = vec4(color, min(alpha, 1.0));
  } else {
    gl_FragColor = vec4(color, 1.0);
  }
}
`;

const DEFAULT_FOCAL: [number, number] = [0.5, 0.52];
const DEFAULT_ROTATION: [number, number] = [1, 0];

type GalaxyProps = HTMLAttributes<HTMLDivElement> & {
  focal?: [number, number];
  rotation?: [number, number];
  starSpeed?: number;
  density?: number;
  hueShift?: number;
  disableAnimation?: boolean;
  speed?: number;
  mouseInteraction?: boolean;
  glowIntensity?: number;
  saturation?: number;
  mouseRepulsion?: boolean;
  repulsionStrength?: number;
  twinkleIntensity?: number;
  rotationSpeed?: number;
  autoCenterRepulsion?: number;
  transparent?: boolean;
  lightMode?: boolean;
};

const Galaxy = ({
  focal = DEFAULT_FOCAL,
  rotation = DEFAULT_ROTATION,
  starSpeed = 0.22,
  density = 1.25,
  hueShift = 8,
  disableAnimation = false,
  speed = 0.34,
  mouseInteraction = true,
  glowIntensity = 0.24,
  saturation = 0.34,
  mouseRepulsion = false,
  repulsionStrength = 0.5,
  twinkleIntensity = 0.28,
  rotationSpeed = 0.002,
  autoCenterRepulsion = 0,
  transparent = true,
  lightMode = false,
  className = "",
  ...rest
}: GalaxyProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    let renderer: Renderer;
    try {
      renderer = new Renderer({
        alpha: transparent,
        antialias: false,
        depth: false,
        dpr: Math.min(window.devicePixelRatio || 1, 1.5),
        premultipliedAlpha: false,
        powerPreference: "high-performance",
      });
    } catch {
      container.dataset.renderState = "unavailable";
      return undefined;
    }

    const gl = renderer.gl;
    const geometry = new Triangle(gl);
    const program = new Program(gl, {
      vertex: vertexShader,
      fragment: fragmentShader,
      transparent,
      depthTest: false,
      depthWrite: false,
      uniforms: {
        uTime: { value: 0 },
        uResolution: { value: new Color(1, 1, 1) },
        uFocal: { value: new Float32Array(focal) },
        uRotation: { value: new Float32Array(rotation) },
        uStarSpeed: { value: starSpeed },
        uDensity: { value: density },
        uHueShift: { value: hueShift },
        uSpeed: { value: speed },
        uMouse: { value: new Float32Array([0.5, 0.5]) },
        uGlowIntensity: { value: glowIntensity },
        uSaturation: { value: saturation },
        uMouseRepulsion: { value: mouseRepulsion },
        uTwinkleIntensity: { value: twinkleIntensity },
        uRotationSpeed: { value: rotationSpeed },
        uRepulsionStrength: { value: repulsionStrength },
        uMouseActiveFactor: { value: 0 },
        uAutoCenterRepulsion: { value: autoCenterRepulsion },
        uTransparent: { value: transparent },
        uLightMode: { value: lightMode ? 1 : 0 },
      },
    });
    const mesh = new Mesh(gl, { geometry, program });
    const canvas = gl.canvas;
    const targetMouse = { x: 0.5, y: 0.5 };
    const smoothMouse = { x: 0.5, y: 0.5 };
    let targetMouseActivity = 0;
    let smoothMouseActivity = 0;
    let animationId: number | null = null;
    let disposed = false;

    canvas.setAttribute("aria-hidden", "true");
    container.appendChild(canvas);
    container.dataset.renderState = "ready";

    if (lightMode) {
      gl.clearColor(1, 1, 1, 1);
    } else if (transparent) {
      gl.enable(gl.BLEND);
      gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
      gl.clearColor(0, 0, 0, 0);
    } else {
      gl.clearColor(0, 0, 0, 1);
    }

    const renderFrame = (time: number) => {
      program.uniforms.uTime.value = time * 0.001;
      smoothMouse.x += (targetMouse.x - smoothMouse.x) * 0.045;
      smoothMouse.y += (targetMouse.y - smoothMouse.y) * 0.045;
      smoothMouseActivity += (targetMouseActivity - smoothMouseActivity) * 0.045;
      program.uniforms.uMouse.value[0] = smoothMouse.x;
      program.uniforms.uMouse.value[1] = smoothMouse.y;
      program.uniforms.uMouseActiveFactor.value = smoothMouseActivity;
      renderer.render({ scene: mesh });
    };

    const animate = (time: number) => {
      if (disposed || document.hidden) {
        animationId = null;
        return;
      }
      renderFrame(time);
      animationId = window.requestAnimationFrame(animate);
    };

    const startAnimation = () => {
      if (!disableAnimation && !document.hidden && animationId === null) {
        animationId = window.requestAnimationFrame(animate);
      }
    };

    const resize = () => {
      const width = Math.max(container.clientWidth, 1);
      const height = Math.max(container.clientHeight, 1);
      renderer.setSize(width, height);
      program.uniforms.uResolution.value = new Color(
        gl.canvas.width,
        gl.canvas.height,
        gl.canvas.width / gl.canvas.height,
      );
      if (disableAnimation) renderFrame(0);
    };

    const handlePointerMove = (event: PointerEvent) => {
      if (event.pointerType === "touch") return;
      const rect = container.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;
      targetMouse.x = (event.clientX - rect.left) / rect.width;
      targetMouse.y = 1 - (event.clientY - rect.top) / rect.height;
      targetMouseActivity = 1;
    };

    const handlePointerOut = (event: PointerEvent) => {
      if (!event.relatedTarget) targetMouseActivity = 0;
    };

    const handleVisibilityChange = () => {
      if (document.hidden && animationId !== null) {
        window.cancelAnimationFrame(animationId);
        animationId = null;
      } else {
        startAnimation();
      }
    };

    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(container);
    resize();

    if (mouseInteraction && !disableAnimation) {
      window.addEventListener("pointermove", handlePointerMove, { passive: true });
      window.addEventListener("pointerout", handlePointerOut, { passive: true });
    }
    document.addEventListener("visibilitychange", handleVisibilityChange);

    if (disableAnimation) renderFrame(0);
    else startAnimation();

    return () => {
      disposed = true;
      if (animationId !== null) window.cancelAnimationFrame(animationId);
      resizeObserver.disconnect();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerout", handlePointerOut);
      program.remove();
      geometry.remove();
      if (canvas.parentNode === container) container.removeChild(canvas);
      gl.getExtension("WEBGL_lose_context")?.loseContext();
    };
  }, [
    autoCenterRepulsion,
    density,
    disableAnimation,
    focal,
    glowIntensity,
    hueShift,
    lightMode,
    mouseInteraction,
    mouseRepulsion,
    repulsionStrength,
    rotation,
    rotationSpeed,
    saturation,
    speed,
    starSpeed,
    transparent,
    twinkleIntensity,
  ]);

  return (
    <div
      ref={containerRef}
      className={`galaxy-container ${className}`.trim()}
      data-animated={!disableAnimation}
      aria-hidden="true"
      {...rest}
    />
  );
};

export default Galaxy;
